import React, { useCallback, useEffect, useRef, useState } from 'react';
import { regenApi } from '../lib/api';
import { toDownloadUrl } from '../lib/utils';
import { downscaleImage } from '../lib/downscaleImage';
import { AssetLibraryPage } from './AssetLibraryPage';
import { StoryboardImages } from '../components/StoryboardImages';
import { Sparkles, Mic, Captions, AlertTriangle, CheckCircle2, Check, Download, User, Film,
  Target, DollarSign, Folder, FileText, LibraryBig, ChevronDown, ChevronUp,
  Copy, Package, Shuffle, X, Loader2, Upload, Trophy, Link2 } from 'lucide-react';

// Vibelets-style preset cards. Each opens a source-picker MODAL (Uploads / Library /
// Your winning ads) defaulting to a relevant tab; "Recreate This" attaches the picked
// source + the prefilled command and dispatches the EXISTING /regen/generate path.
type ModalTab = 'uploads' | 'library' | 'winners' | 'scraper';
const CARDS: { label: string; icon: any; prefill: string; tab: ModalTab; productMode?: boolean }[] = [
  { label: 'Clone winning Ads', icon: Copy, tab: 'winners',
    // FAITHFUL clone: same script/location/wardrobe/framing verbatim, ONLY the person changes. The command
    // box is left empty on purpose — it becomes OPTIONAL character tweaks (e.g. "make him a bit younger").
    prefill: '' },
  { label: 'Recreate from Meta Ads', icon: Trophy, tab: 'scraper',
    prefill: 'Recreate this competitor winner as our ad — same hook energy and structure, our offer and a fresh character.' },
  { label: 'Turn Product into Ad', icon: Package, tab: 'uploads', productMode: true,
    prefill: 'Turn my product into a UGC ad — a real person casually explaining the benefit in a selfie video.' },
  { label: 'Reference to ad', icon: Film, tab: 'uploads',
    prefill: "Use this reference's style and pacing to make a new ad for our offer." },
  { label: 'Hook variations', icon: Shuffle, tab: 'winners',
    prefill: 'Give me 3 hook variations of this ad — same body, three different openings.' },
];

// ── Live Creative-Office feed for the job being generated RIGHT HERE ──────────────────────────
// Polls /regen/creative-team/activity?job_id=<jobId> and streams the desks lighting up + each step
// (Video Router → Director → Copywriter → Scene → Character → Prompt → Critic → Seedance). Makes the
// creation visible in AutoStudio (the office room otherwise only lived on the Studio page).
const OFFICE_EMOJI: Record<string, string> = {
  router: '🧭', director: '🎬', strategist: '🧠', scriptwriter: '✍️', scene: '🎭',
  character: '🧑', shots: '🎥', prompt: '📝', critic: '🔍', learner: '📚', finance: '💰',
};
function LiveOfficeFeed({ jobId }: { jobId: string }) {
  const [feed, setFeed] = useState<any[]>([]);
  const [desks, setDesks] = useState<any[]>([]);
  useEffect(() => {
    if (!jobId) return;
    let alive = true;
    const pull = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      regenApi.teamActivity(jobId).then(r => {
        if (!alive) return;
        setFeed(r.data.feed || []); setDesks(r.data.desks || []);
      }).catch(() => { /* transient */ });
    };
    pull();
    const t = setInterval(pull, 3500);
    return () => { alive = false; clearInterval(t); };
  }, [jobId]);
  if (!feed.length && !desks.length) return null;
  return (
    <div className="rounded-lg border border-border bg-background/40 p-2.5">
      <div className="text-[11px] font-medium text-gray-500 mb-1.5">Creative team — live</div>
      {desks.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {desks.map((d: any) => (
            <span key={d.id}
              className={`text-[10px] px-1.5 py-0.5 rounded-full border ${d.status === 'working'
                ? 'border-emerald-500 text-emerald-600 bg-emerald-500/10' : 'border-border text-gray-400'}`}>
              {OFFICE_EMOJI[d.id] || '•'} {d.role || d.id}{d.status === 'working' ? '…' : ''}
            </span>
          ))}
        </div>
      )}
      <div className="space-y-0.5 max-h-44 overflow-y-auto">
        {feed.slice(0, 16).map((e: any, i: number) => (
          <div key={i} className="text-[11px] text-gray-500 flex items-start gap-1.5 leading-snug">
            <span className="shrink-0">{OFFICE_EMOJI[e.persona] || '•'}</span>
            <span className="text-gray-400 shrink-0 w-16 truncate">{e.role || e.persona}</span>
            <span className="truncate">
              {e.event === 'done' ? '✓ ' : e.event === 'fail' ? '✗ ' : e.event === 'revise' ? '↻ ' : ''}
              {e.task || e.detail || e.event}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Self-contained media tile (crisp thumbnails from S3, not our server)
function Media({ url, className }: { url?: string; className?: string }) {
  if (!url) return <div className={`${className} bg-black/10 flex items-center justify-center text-[11px] text-gray-400`}>no preview</div>;
  if (/\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(url)) return <img src={url} alt="" className={(className || '').replace(/object-cover/g, 'object-contain')} loading="lazy" />;
  const sep = url.includes('#') ? '' : '#t=0.1';
  return <video src={`${url}${sep}`} controls preload="metadata" playsInline muted className={className} />;
}

// ── Conversational Recreate — a Studio-style chat for cloning a winner ─────────────────────────
// Opens on "Recreate This": shows the clip preview + the plan ("everything copied, only the person
// changes"), then lets the user reply to refine ("make him younger", "keep the cap", "set it in TX")
// or say "go". Talks to the /studio/chat recreate gate (recreate_source_url → awaiting_recreate_confirm).
// Opening the panel spends NOTHING — it only dispatches a generation when the user says go.
function RecreateChat({ source, vertical, onClose, onDispatched }: {
  source: { videoUrl: string; label?: string };
  vertical?: string;
  onClose: () => void;
  onDispatched: (jobId: string) => void;
}) {
  const [threadId, setThreadId] = useState('');
  const [msgs, setMsgs] = useState<{ role: 'user' | 'assistant'; text: string }[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  const post = async (message: string, first = false) => {
    setSending(true);
    try {
      const r = await regenApi.studioChat({
        message, thread_id: threadId || undefined, vertical,
        ...(first ? { recreate_source_url: source.videoUrl, recreate_label: source.label } : {}),
      });
      if (r.data.thread_id && !threadId) setThreadId(r.data.thread_id);
      const out: any[] = r.data.messages || [];
      setMsgs(prev => [...prev, ...out.filter(m => m.text).map(m => ({ role: 'assistant' as const, text: m.text }))]);
      const vid = out.find(m => (m.data?.request_id || m.request_id));
      if (vid) onDispatched(vid.data?.request_id || vid.request_id);
    } catch {
      setMsgs(prev => [...prev, { role: 'assistant', text: 'Something went wrong — try again in a moment.' }]);
    }
    setSending(false);
  };

  useEffect(() => { if (!started.current) { started.current = true; post('Recreate this', true); } }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [msgs, sending]);

  const send = () => { const t = input.trim(); if (!t || sending) return; setMsgs(p => [...p, { role: 'user', text: t }]); setInput(''); post(t); };

  return (
    <div className="rounded-2xl border border-primary/40 bg-card p-3 space-y-3">
      <div className="flex items-center gap-2.5">
        <div className="w-11 h-[70px] shrink-0 rounded-md overflow-hidden border border-border bg-black">
          <Media url={source.videoUrl} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">Recreate: {source.label || 'winning ad'}</div>
          <div className="text-[11px] text-gray-500">Same script, location &amp; style — only the person changes.</div>
        </div>
        <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-accent shrink-0" title="Close"><X className="w-4 h-4" /></button>
      </div>
      <div ref={scrollRef} className="max-h-72 overflow-y-auto space-y-2 pr-1">
        {msgs.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'text-right' : ''}>
            <span className={`inline-block max-w-[85%] text-left px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-primary text-white' : 'bg-accent text-gray-700 dark:text-gray-200'}`}>{m.text}</span>
          </div>
        ))}
        {sending && <div className="text-xs text-gray-500 px-1">…thinking</div>}
      </div>
      <div className="flex items-center gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
          placeholder='Add a tweak — "make him younger", "keep the cap"… or just hit Generate'
          className="flex-1 px-3 py-2 rounded-xl border border-border bg-transparent text-sm placeholder:text-gray-500 focus:outline-none" />
        {input.trim()
          ? <button onClick={send} disabled={sending}
              className="px-4 py-2 rounded-xl bg-accent text-gray-700 dark:text-gray-200 text-sm font-semibold disabled:opacity-50">Send</button>
          : <button onClick={() => { if (sending) return; setMsgs(p => [...p, { role: 'user', text: 'Generate' }]); post('go'); }} disabled={sending}
              title="Generate the recreation now" className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1">
              <Film className="w-3.5 h-3.5" /> Generate</button>}
      </div>
    </div>
  );
}

const EXAMPLES = [
  'Home insurance ad — a 45-55 woman explaining coverage for $29/month',
  'Bizop hook — young woman, 20s, work-from-home, casual, 25s',
  'Refinance: senior man explaining how he saved $400 on his mortgage',
];

// Friendly names for the TTS engines the voice catalog can come back with.
const VOICE_ENGINES: Record<string, string> = {
  elevenlabs: 'ElevenLabs', openai: 'OpenAI', gemini: 'Gemini', deepgram: 'Deepgram',
  kokoro: 'Kokoro', chatterbox: 'Chatterbox', 'fal-clone': 'fal (voice clone)',
};

export function AutoStudio() {
  const [view, setView] = useState<'compose' | 'storyboard'>('compose');   // top-of-view toggle: composer vs saved storyboard stills
  const [command, setCommand] = useState('');
  const [seconds, setSeconds] = useState(20);
  const [format, setFormat] = useState<'ugc' | 'ugc_broll'>('ugc');   // simple UGC talking-head vs UGC + B-Roll composite
  const [refSource, setRefSource] = useState<{ videoUrl?: string; imageFile?: File; note?: string; label?: string } | null>(null);   // reference selected via "Recreate This"
  const [recreateChat, setRecreateChat] = useState<{ videoUrl: string; label?: string } | null>(null);   // conversational clone panel (video winners)
  const [cloneVoice, setCloneVoice] = useState(false);   // opt-in (cloning needs Replicate/paid)
  const [captions, setCaptions] = useState(true);        // burn word-timed captions — every DR ad has them, so ON by default
  const [voiceId, setVoiceId] = useState('');           // '' = auto-cast / clone
  const [voiceProvider, setVoiceProvider] = useState(''); // '' = any engine (filters the voice list only)
  const [assetId, setAssetId] = useState('');           // '' = auto-pick from library
  const [voices, setVoices] = useState<any[]>([]);
  const [cloneReady, setCloneReady] = useState(true);   // is voice-cloning actually configured?
  const [avatars, setAvatars] = useState<any[]>([]);
  const [charQ, setCharQ] = useState('');               // search the character library
  const [charShown, setCharShown] = useState(12);
  const [brolls, setBrolls] = useState<any[]>([]);      // library b-roll/hook clips (for UGC + B-Roll)
  const [brollIds, setBrollIds] = useState<string[]>([]); // user-picked b-roll clips; [] = engine auto-casts
  const [brollQ, setBrollQ] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [providers, setProviders] = useState<any>({ lipsync: [] });   // only configured providers
  const [lipProvider, setLipProvider] = useState('');                 // '' = Auto (brain, cheapest)
  const [quality, setQuality] = useState('bulk');

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [voiceGate, setVoiceGate] = useState<any>(null);   // voice↔character mismatch — needs your call
  const [lenGate, setLenGate] = useState<{ need: number } | null>(null);  // script↔Length mismatch — your call
  const [jobId, setJobId] = useState('');
  const [resolved, setResolved] = useState<any>(null);
  const [job, setJob] = useState<any>(null);            // the live generation row
  const pollRef = useRef<any>(null);
  const [modal, setModal] = useState<null | { tab: ModalTab; productMode: boolean }>(null);

  // "Recreate This": attach the picked source + the (prefilled/edited) command and dispatch the
  // SAME /regen/generate path the Creative Library "remix" uses — a video reference via video_urls,
  // an uploaded reference image via the images file field. The job lands in generations(), so the
  // existing poll + result panel below render it with no extra plumbing. Throws on failure so the
  // modal can surface the error and stay open.
  const recreate = async (src: { videoUrl?: string; imageFile?: File; note?: string; label?: string }) => {
    // A VIDEO winner → open the conversational clone panel (Studio-style): preview + plan ("everything
    // copied, only the person changes"), the user refines/goes. Nothing generates until they say "go".
    if (src.videoUrl) {
      setRecreateChat({ videoUrl: src.videoUrl, label: src.label });
      setRefSource(null); setJob(null); setJobId(''); setResolved(null);
      setModal(null);
      return;
    }
    // An IMAGE reference → the existing i2v flow (user fills the brief, then Generate).
    setRefSource(src);
    if (src.note && !command.trim()) setCommand(src.note);
    setModal(null);
  };

  // load voice catalog + lip-sync-ready avatars for the optional pickers
  useEffect(() => {
    regenApi.listVoices().then(r => {
      setVoices(r.data.voices || []);
      setCloneReady(r.data.clone_available !== false);
    }).catch(() => {});
    regenApi.getAssets({ kind: 'avatar', usable_as: 'avatar_lipsync', page_size: 300 }).then(r => setAvatars(r.data.data || [])).catch(() => {});
    regenApi.getAssets({ usable_as: 'broll', page_size: 300 }).then(r => setBrolls(r.data.data || [])).catch(() => {});
    regenApi.providers().then(r => { setProviders(r.data || { lipsync: [] }); if (r.data?.default_quality) setQuality(r.data.default_quality); }).catch(() => {});
  }, []);

  // Voice ENGINE picker. The engine only ever ships a voice_id (that is the single param the backend
  // and AE accept), so choosing an engine filters the catalog to that provider's voices — ElevenLabs
  // included. ElevenLabs is always listed so it's visible as an option; it's only selectable when the
  // engine actually returned 11Labs voices (i.e. ELEVENLABS_API_KEY is configured there).
  const voiceEngines = React.useMemo(() => {
    const seen = Array.from(new Set(voices.map(v => String(v.provider || '')).filter(Boolean)));
    if (!seen.includes('elevenlabs')) seen.push('elevenlabs');
    return seen.sort();
  }, [voices]);
  const engineVoices = React.useMemo(
    () => (voiceProvider ? voices.filter(v => v.provider === voiceProvider) : voices),
    [voices, voiceProvider]);

  // searchable character pool (name, description, setting, vibe, vertical, age/gender)
  const filteredAvatars = React.useMemo(() => {
    const q = charQ.trim().toLowerCase();
    if (!q) return avatars;
    return avatars.filter(a => [a.name, a.character_desc, a.scene, a.style, a.emotion, a.vertical, a.age_band, a.gender]
      .filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [avatars, charQ]);
  const filteredBrolls = React.useMemo(() => {
    const q = brollQ.trim().toLowerCase();
    if (!q) return brolls;
    return brolls.filter(a => [a.name, a.scene, a.vertical, a.kind].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [brolls, brollQ]);

  const poll = useCallback((id: string) => {
    // Fetch ONLY this request (was: pull all 500 generations to find one by id — a 429 amplifier on a poll).
    regenApi.getRequest(id).then(r => {
      const row = r.data?.data;
      if (row) {
        setJob(row);
        if (row.status !== 'running') { clearInterval(pollRef.current); }
      }
    }).catch(() => {});
  }, []);

  // A pasted script has a natural spoken length (~2.5 words/sec). If that is far longer than the
  // Length box, we USED TO silently render a fragment — the user saw "half the script spoken" and
  // had no idea why. Now we ASK: stretch the video to fit the script, or condense the script to fit
  // the time. Never guess, never truncate silently.
  const estimateSeconds = (t: string) => Math.ceil((t || '').trim().split(/\s+/).filter(Boolean).length / 2.5);
  const submit = async (opts?: { confirm_voice?: boolean; voice_id?: string;
                                 lengthChoice?: 'fit_script' | 'condense_script' }) => {
    // A video recreate needs NO typed command — the winner's own transcript is the script; the command
    // box is only optional character tweaks. Every other path still requires a command.
    const _isVideoRecreate = !!refSource?.videoUrl;
    if ((!command.trim() && !_isVideoRecreate) || busy) return;
    // Skip the script-length gate for a recreate: the winner's transcript sets the length, not the command.
    if (!opts?.lengthChoice && !_isVideoRecreate) {
      const need = estimateSeconds(command);
      if (need > Math.round(seconds * 1.25) && need >= 12) { setLenGate({ need }); return; }
    }
    let useSeconds = seconds;
    if (opts?.lengthChoice === 'fit_script') useSeconds = Math.min(90, estimateSeconds(command));
    setBusy(true); setErr(''); setVoiceGate(null); setLenGate(null); setResolved(null); setJob(null); setJobId('');
    try {
      // RECREATE FROM A SELECTED REFERENCE — with the user's own inputs (script=command, type=format,
      // duration). Uses the /regen/generate remix path; the job lands in generations() below.
      if (refSource) {
        const fd = new FormData();
        fd.append('engine', 'seedance');
        fd.append('seconds', String(useSeconds));
        fd.append('request_type', format);
        fd.append('captions', String(captions));
        if (refSource.videoUrl) {
          fd.append('video_urls', JSON.stringify([refSource.videoUrl]));
          // FAITHFUL CLONE, NEW CHARACTER: AE transcribes + vision-locks the winner and reproduces
          // EVERYTHING but the person (verbatim script, same location/wardrobe/framing/pacing). The
          // command box (if the user typed anything) is the optional character steer, e.g. "younger".
          fd.append('clone_mode', 'faithful_new_character');
          fd.append('download_url', refSource.videoUrl);
          fd.append('prompt', command.trim() || 'Recreate this winning ad faithfully — same script, location and style, a new person.');
          if (command.trim()) fd.append('character_steer', command.trim());
        } else {
          fd.append('prompt', command.trim());
        }
        // Downscale a full-size phone photo BEFORE upload so it doesn't trip the "file too large" cap.
        if (refSource.imageFile) fd.append('images', await downscaleImage(refSource.imageFile));
        const rr = await regenApi.generate(fd);
        const gid = rr.data.data?.id;
        setJobId(gid || '');
        if (gid) { clearInterval(pollRef.current); pollRef.current = setInterval(() => poll(gid), 6000); poll(gid); }
        setBusy(false);
        return;
      }
      const useVoice = opts?.voice_id !== undefined ? opts.voice_id : voiceId;
      const r = await regenApi.autoLipsync({
        command: command.trim(), seconds: useSeconds,
        // condense = let the writer rewrite the script to fit the time, keeping the core message
        ...(opts?.lengthChoice === 'condense_script' ? { script_mode: 'modify' as const } : {}),
        clone_voice: cloneVoice && !useVoice,
        voice_id: useVoice || undefined,
        asset_id: assetId || undefined,
        captions,
        lipsync_provider: lipProvider || undefined,
        quality,
        request_type: format,   // 'ugc' | 'ugc_broll' → CL routes ugc_broll to the b-roll composite
        ...(format === 'ugc_broll' && brollIds.length ? { broll_asset_ids: brollIds.join(',') } : {}),  // user-picked b-roll (engine arranges)
        confirm_voice: opts?.confirm_voice || undefined,
      });
      setResolved(r.data.resolved);
      const id = r.data.data?.id;
      setJobId(id);
      if (id) {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(() => poll(id), 6000);
        poll(id);
      }
    } catch (e: any) {
      // 409 = the voice you picked doesn't suit the character — decide before we spend
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.needs_confirmation) { setVoiceGate(d); setResolved(d.resolved); }
      else setErr(d?.error || e.message || 'failed');
    } finally { setBusy(false); }
  };
  useEffect(() => () => clearInterval(pollRef.current), []);

  const variants = job?.variants || [];
  const done = job && job.status !== 'running';

  return (
    <div className="space-y-6">
      {/* top-of-view toggle — compose the ad, or browse the saved storyboard stills */}
      <div className="flex items-center gap-1 w-fit rounded-full border border-border bg-card p-0.5">
        {([['compose', 'Compose'], ['storyboard', 'Storyboard Images']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              view === k ? 'bg-primary/15 text-primary' : 'text-gray-400 hover:text-gray-200'}`}
          >{label}</button>
        ))}
      </div>

      {view === 'storyboard' && <StoryboardImages />}

      <div className={view === 'storyboard' ? 'hidden' : 'space-y-6'}>
      {/* hero */}
      <div className="text-center pt-1">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create winning ads</h1>
        <p className="text-sm text-gray-500 mt-1.5 max-w-2xl mx-auto">Describe the ad in plain English. ORBIT casts a real character
          from your library, writes the script with your offer, matches a voice, and lip-syncs it naturally.</p>
      </div>

      {/* preset cards — open a source-picker modal, prefilling the command */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {CARDS.map(p => (
          <button key={p.label} onClick={() => { setCommand(p.prefill); setModal({ tab: p.tab, productMode: !!p.productMode }); }}
            className="group flex flex-col items-start gap-2.5 p-3.5 rounded-2xl border border-border bg-card hover:border-primary/50 transition text-left">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary group-hover:bg-primary group-hover:text-white transition">
              <p.icon className="w-5 h-5" />
            </span>
            <span className="text-xs font-medium text-gray-900 dark:text-white leading-snug">{p.label}</span>
          </button>
        ))}
      </div>

      {modal && (
        <RecreateModal
          initialTab={modal.tab}
          productMode={modal.productMode}
          command={command}
          onCommand={setCommand}
          onClose={() => setModal(null)}
          onRecreate={recreate}
        />
      )}

      {/* Conversational clone panel — opens on "Recreate This" for a video winner. Replaces the prompt box. */}
      {recreateChat && (
        <RecreateChat
          source={recreateChat}
          onClose={() => setRecreateChat(null)}
          onDispatched={jid => {
            setRecreateChat(null); setJobId(jid);
            clearInterval(pollRef.current);
            pollRef.current = setInterval(() => poll(jid), 6000);
            poll(jid);
          }}
        />
      )}

      {/* big prompt box */}
      <div className={recreateChat ? 'hidden' : 'rounded-2xl border border-border bg-card p-4 space-y-3'}>
        <textarea value={command} onChange={e => setCommand(e.target.value)} rows={3}
          placeholder={refSource?.videoUrl
            ? 'Optional tweaks for the new person — e.g. "make him a bit younger", "keep the cap", "set it in Texas". Leave blank for an exact clone.'
            : 'Describe your ad… e.g. "Home insurance ad with a 45-55 woman explaining coverage for $29/month"'}
          className="w-full resize-none bg-transparent text-base focus:outline-none placeholder:text-gray-500" />
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex, i) => (
            <button key={i} onClick={() => setCommand(ex)} className="text-[11px] px-2.5 py-1 rounded-full border border-border text-gray-500 hover:bg-accent">{ex.slice(0, 42)}…</button>
          ))}
        </div>

        {/* Selected reference (from "Recreate This") — the user fills the script/type/duration below, then Generate */}
        {refSource && (
          <div className="flex items-center gap-2 mt-2 p-2 rounded-xl border border-primary/40 bg-primary/5 text-sm">
            <Sparkles className="w-4 h-4 text-primary shrink-0" />
            <span className="text-gray-700 dark:text-gray-200 truncate">Recreating from: <b>{refSource.label || refSource.note || (refSource.imageFile ? refSource.imageFile.name : 'selected reference')}</b></span>
            <span className="text-[11px] text-gray-500 hidden sm:inline">{refSource.videoUrl ? '— exact clone: same script, location & style; only the person changes. Optional tweaks below, then Generate.' : '— set your script + type below, then Generate'}</span>
            <button onClick={() => setRefSource(null)} className="ml-auto p-0.5 rounded hover:bg-accent shrink-0" title="Remove reference"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* primary action row */}
        <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-border/60">
          <label className="text-sm text-gray-500 flex items-center gap-1.5">Length
            <input type="number" min={6} max={90} value={seconds} onChange={e => setSeconds(parseInt(e.target.value) || 20)}
              className="w-16 px-2 py-1 rounded-lg border border-border bg-transparent" />s</label>
          {/* Format: simple UGC talking-head vs UGC + B-Roll (satisfaction opener + interior inserts over the talking-head) */}
          <label className="text-sm text-gray-500 flex items-center gap-1.5" title="UGC = a straight talking-head. UGC + B-Roll = a satisfaction b-roll opener + interior inserts intercut over the talking-head, like the reference ads.">Format
            <select value={format} onChange={e => setFormat(e.target.value as 'ugc' | 'ugc_broll')}
              className="px-2 py-1 rounded-lg border border-border bg-transparent text-sm">
              <option value="ugc">UGC (talking-head)</option>
              <option value="ugc_broll">UGC + B-Roll</option>
            </select>
          </label>
          <button onClick={() => setShowAdvanced(s => !s)} className="text-sm text-gray-500 hover:text-gray-200 inline-flex items-center gap-1">
            Advanced {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button onClick={() => submit()} disabled={busy || (!command.trim() && !refSource?.videoUrl)}
            className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50">
            <Sparkles className="w-4 h-4" /> {busy ? 'Dispatching…' : (refSource?.videoUrl ? 'Recreate' : 'Generate')}
          </button>
        </div>

        {/* advanced options — decluttered under a toggle */}
        {showAdvanced && (
          <div className="space-y-3 pt-1">
            <div className="flex items-center gap-4 flex-wrap">
              <label className={`text-sm flex items-center gap-1.5 ${cloneReady ? 'text-gray-500 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
                title={cloneReady ? 'Clones the character’s real voice from their own footage (paid)'
                                  : 'Voice cloning needs Chatterbox or a funded Replicate account — not configured, so this would silently fall back to a preset voice'}>
                <input type="checkbox" checked={cloneVoice && cloneReady} disabled={!!voiceId || !cloneReady}
                  onChange={e => setCloneVoice(e.target.checked)} />
                <Mic className="w-4 h-4" /> clone the character's own voice (paid)
                {!cloneReady && ' — unavailable'}{voiceId && cloneReady && ' (off — voice chosen)'}
              </label>
              <label className="text-sm text-gray-500 flex items-center gap-1.5 cursor-pointer" title="Accurate word-timed captions forced-aligned to the script (free, ffmpeg)">
                <input type="checkbox" checked={captions} onChange={e => setCaptions(e.target.checked)} /> <Captions className="w-4 h-4" /> burn captions
              </label>
              <label className="text-sm text-gray-500 flex items-center gap-1.5">lip-sync
                <select value={lipProvider} onChange={e => setLipProvider(e.target.value)}
                  className="px-2 py-1 rounded-lg border border-border bg-transparent">
                  <option value="">Auto (brain · cheapest)</option>
                  {(providers.lipsync || []).map((p: any) => <option key={p.id} value={p.id}>{p.name} {p.cost}</option>)}
                </select></label>
              <label className="text-sm text-gray-500 flex items-center gap-1.5">quality
                <select value={quality} onChange={e => setQuality(e.target.value)}
                  className="px-2 py-1 rounded-lg border border-border bg-transparent">
                  <option value="bulk">bulk (cheapest)</option><option value="premium">premium</option>
                </select></label>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] text-gray-500 mb-1">Voice ({voiceId ? '1 chosen' : 'auto-cast'})</div>
                <select value={voiceProvider}
                  onChange={e => { setVoiceProvider(e.target.value); setVoiceId(''); }}
                  className="w-full text-xs px-2 py-1.5 mb-2 rounded-lg border border-border bg-transparent">
                  <option value="">Any voice engine</option>
                  {voiceEngines.map(p => {
                    const n = voices.filter(v => v.provider === p).length;
                    return <option key={p} value={p} disabled={!n}>
                      {VOICE_ENGINES[p] || p}{n ? ` (${n})` : ' — not configured'}</option>;
                  })}
                </select>
                <select value={voiceId} onChange={e => setVoiceId(e.target.value)}
                  className="w-full text-xs px-2 py-1.5 rounded-lg border border-border bg-transparent">
                  <option value="">Auto (brain casts / clone)</option>
                  {engineVoices.map(v => <option key={v.id} value={v.id}>{v.name} · {v.gender}/{v.age_band} · {v.provider}{v.cloned ? ' · cloned' : ''}</option>)}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] text-gray-500">
                    Character ({assetId ? (avatars.find(a => a.id === assetId)?.character_desc || avatars.find(a => a.id === assetId)?.name || '1 chosen') : 'auto-pick — ORBIT casts by tone & setting'})
                  </div>
                  {assetId && <button onClick={() => setAssetId('')} className="text-[11px] text-primary hover:underline">clear</button>}
                </div>
                <input value={charQ} onChange={e => { setCharQ(e.target.value); setCharShown(12); }}
                  placeholder="search characters — name, setting, vibe, vertical…"
                  className="w-full text-xs px-2 py-1.5 mb-2 rounded-lg border border-border bg-transparent" />
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-72 overflow-y-auto pr-1">
                  {filteredAvatars.slice(0, charShown).map(a => (
                    <button key={a.id} onClick={() => setAssetId(assetId === a.id ? '' : a.id)}
                      title={`${a.character_desc || a.name} · ${a.age_band || '—'}/${a.gender || '—'}${a.scene ? ' · ' + a.scene : ''}`}
                      className={`relative rounded-lg overflow-hidden border ${assetId === a.id ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-primary/50'}`}>
                      <Media url={a.play_url} className="w-full aspect-[9/16] object-cover bg-black" />
                      <span className={`absolute top-1 left-1 w-4 h-4 rounded border flex items-center justify-center
                        ${assetId === a.id ? 'bg-primary border-primary text-white' : 'bg-black/50 border-white/60 text-transparent'}`}><Check className="w-3 h-3" /></span>
                      <span className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-black/70 text-white text-[9px] truncate">
                        {a.character_desc || a.name}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-1 text-[11px] text-gray-500">
                  <span>{filteredAvatars.length} lip-sync-ready character{filteredAvatars.length === 1 ? '' : 's'}</span>
                  {charShown < filteredAvatars.length &&
                    <button onClick={() => setCharShown(n => n + 24)} className="text-primary hover:underline">show more</button>}
                </div>
              </div>
            </div>
            {/* UGC + B-Roll: let the user hand the engine the b-roll clips (multi-select). Empty = auto-cast. */}
            {format === 'ugc_broll' && (
              <div className="mt-3 pt-3 border-t border-border/60">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] text-gray-500">
                    B-Roll clips ({brollIds.length ? `${brollIds.length} chosen — ORBIT arranges them (opener + inserts)` : 'auto-cast from the library'})
                  </div>
                  {brollIds.length > 0 && <button onClick={() => setBrollIds([])} className="text-[11px] text-primary hover:underline">clear</button>}
                </div>
                <input value={brollQ} onChange={e => setBrollQ(e.target.value)}
                  placeholder="search b-roll — scene, vertical, kind…"
                  className="w-full text-xs px-2 py-1.5 mb-2 rounded-lg border border-border bg-transparent" />
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-72 overflow-y-auto pr-1">
                  {filteredBrolls.slice(0, 60).map(b => {
                    const on = brollIds.includes(b.id);
                    return (
                      <button key={b.id} onClick={() => setBrollIds(ids => on ? ids.filter(x => x !== b.id) : [...ids, b.id])}
                        title={`${b.scene || b.name} · ${b.kind || 'broll'}${b.vertical ? ' · ' + b.vertical : ''}`}
                        className={`relative rounded-lg overflow-hidden border ${on ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-primary/50'}`}>
                        <Media url={b.play_url} className="w-full aspect-[9/16] object-cover bg-black" />
                        <span className={`absolute top-1 left-1 w-4 h-4 rounded border flex items-center justify-center ${on ? 'bg-primary border-primary text-white' : 'bg-black/50 border-white/60 text-transparent'}`}><Check className="w-3 h-3" /></span>
                        <span className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-black/70 text-white text-[9px] truncate">{b.scene || b.name}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-gray-400 mt-1">Pick multiple clips (or leave empty to auto-cast) — ORBIT decides the opener + interior inserts and composes the best UGC + B-Roll.</div>
              </div>
            )}
          </div>
        )}
        {err && <div className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

        {/* script ↔ Length mismatch — ASK instead of silently rendering a fragment */}
        {lenGate && (
          <div className="rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
            <div className="text-sm font-medium text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> Your script is longer than the length you set
            </div>
            <div className="text-xs text-amber-800/90 dark:text-amber-200/80">
              This script reads as about <b>{lenGate.need}s</b> of speech, but Length is set to <b>{seconds}s</b>.
              Generating now would cut it off partway. How do you want it?
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button onClick={() => submit({ lengthChoice: 'fit_script' })}
                className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white font-medium inline-flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> {lenGate.need > 90
                  ? `Make a ~90s video (the longest here — the rest is trimmed)`
                  : `Make the full ~${lenGate.need}s video (whole script)`}
              </button>
              <button onClick={() => submit({ lengthChoice: 'condense_script' })}
                className="text-xs px-3 py-1.5 rounded-md bg-primary text-white font-medium">
                Shorten the script to fit {seconds}s (keep the core message)
              </button>
              <button onClick={() => setLenGate(null)}
                className="text-xs px-3 py-1.5 rounded-md border border-border text-gray-600 dark:text-gray-300">
                Cancel — I'll edit it myself
              </button>
            </div>
          </div>
        )}

        {/* voice ↔ character mismatch — we stop BEFORE spending and let you decide */}
        {voiceGate && (
          <div className="rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
            <div className="text-sm font-medium text-amber-800 dark:text-amber-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> That voice won't suit this character</div>
            <div className="text-xs text-amber-800/90 dark:text-amber-200/80">{voiceGate.warning}</div>
            {/* Two clear choices, not four: let ORBIT use a fitting voice (its recommendation when it has
                one, else it casts one), or keep the voice you picked. Plus Cancel. */}
            <div className="flex flex-wrap gap-2 pt-1">
              {voiceGate.recommended_voice
                ? <button onClick={() => { setVoiceId(voiceGate.recommended_voice.id); submit({ confirm_voice: true, voice_id: voiceGate.recommended_voice.id }); }}
                    className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white font-medium inline-flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> Use the recommended voice — {voiceGate.recommended_voice.name}
                  </button>
                : <button onClick={() => { setVoiceId(''); submit({ confirm_voice: true, voice_id: '' }); }}
                    className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white font-medium inline-flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" /> Let ORBIT pick a fitting voice
                  </button>}
              <button onClick={() => submit({ confirm_voice: true })}
                className="text-xs px-3 py-1.5 rounded-md border border-border text-gray-500">Use my voice anyway</button>
              <button onClick={() => setVoiceGate(null)} className="text-xs px-3 py-1.5 text-gray-500">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* resolved plan */}
      {resolved && (
        <div className="rounded-xl border border-border bg-card p-3 text-sm space-y-1">
          <div className="font-medium text-gray-900 dark:text-white">What ORBIT locked in</div>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
            <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-gray-400 shrink-0" /> Character: <b>{resolved.character?.name || '—'}</b> ({resolved.character?.age_band}/{resolved.character?.gender}{resolved.character?.face_score ? `, face ${resolved.character.face_score}` : ''})</div>
            <div className="flex items-center gap-1.5"><Film className="w-3.5 h-3.5 text-gray-400 shrink-0" /> Setting: {resolved.character?.setting || '—'}{resolved.character?.vibe ? ` · ${resolved.character.vibe}` : ''}</div>
            {/* why this clip won: tone/energy/vertical/scene fit — so a mismatch is visible BEFORE the spend */}
            <div className="sm:col-span-2 text-[11px] text-gray-500 flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5 shrink-0" /> Cast for <b>{resolved.tone || 'neutral'}</b> tone / <b>{resolved.energy || 'any'}</b> energy
              {resolved.character?.source_vertical && resolved.vertical && resolved.character.source_vertical !== resolved.vertical &&
                <span className="text-amber-600 inline-flex items-center gap-1"> · <AlertTriangle className="w-3 h-3" /> footage is from {resolved.character.source_vertical}, ad is {resolved.vertical}</span>}
            </div>
            {resolved.character?.reference && <div className="sm:col-span-2 text-[10px] text-gray-400 truncate flex items-center gap-1.5"><Folder className="w-3 h-3 shrink-0" /> from your library: <span className="font-mono">{resolved.character.reference}</span></div>}
            <div className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" /> Script: {resolved.script ? <b>{resolved.script.number} — {resolved.script.title}</b> : (resolved.script_found ? 'from library' : 'written fresh from your brief')}</div>
            <div className="flex items-center gap-1.5"><Mic className="w-3.5 h-3.5 text-gray-400 shrink-0" /> Voice: <b>{resolved.voice}</b></div>
            <div className="flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5 text-gray-400 shrink-0" /> Offer: <b>{resolved.offer_value || '—'}</b> · {resolved.seconds}s · {resolved.vertical || 'general'}</div>
          </div>
        </div>
      )}

      {/* result / progress */}
      {jobId && (
        <div className="rounded-xl border border-border bg-card p-3">
          {!done ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Building in the Creative Team Room — watch the team work below (this takes a few minutes)
              </div>
              <LiveOfficeFeed jobId={jobId} />
            </div>
          ) : job.status === 'failed' ? (
            <div className="text-sm text-red-600 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {job.error || 'generation failed'}</div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm font-medium text-emerald-600 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Done</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {variants.map((v: any, i: number) => {
                  const src = v.download_url || v.video_url;
                  return (
                    <div key={i} className="rounded-lg border border-border overflow-hidden bg-black relative group">
                      <Media url={src} className="w-full aspect-[9/16] object-cover" />
                      <a href={toDownloadUrl(src, v.filename)} download
                        className="absolute bottom-1 right-1 text-[11px] px-2 py-1 rounded-md bg-black/70 text-white hover:bg-black flex items-center gap-1">
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Avtar Studio references the asset library, so it lives right here */}
      <div className="pt-4 mt-2 border-t border-border">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-1.5"><LibraryBig className="w-4 h-4" /> Your character & asset library</h3>
        <p className="text-xs text-gray-500 mb-3">ORBIT picks from these — or pick a character above. Search & filter your avatars, maps, b-roll, hooks, voices.</p>
        <AssetLibraryPage embedded />
      </div>
      </div>
    </div>
  );
}

// ROI (not ROAS): profit per $ spent, shown as a %.
const fmtRoi = (roas?: number | null) => roas == null ? '—' : `${roas >= 1 ? '+' : ''}${((roas - 1) * 100).toFixed(0)}%`;
const ModalLoading = () => <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-16"><Loader2 className="w-5 h-5 animate-spin" /> loading…</div>;
const ModalEmpty = ({ text }: { text: string }) => <div className="text-center text-sm text-gray-500 py-16">{text}</div>;

function SourceCard({ url, title, sub, busy, disabled, onGo }:
  { url?: string; title?: string; sub?: string; busy: boolean; disabled: boolean; onGo: () => void }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-black/20 flex flex-col">
      <Media url={url} className="w-full aspect-[9/16] object-cover bg-black" />
      <div className="p-2 space-y-1.5">
        <div className="text-[11px] text-gray-300 truncate">{title || '—'}</div>
        {sub && <div className="text-[11px] text-emerald-500">{sub}</div>}
        <button disabled={disabled} onClick={onGo}
          className="w-full text-[11px] px-2 py-1.5 rounded-lg bg-primary text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-1">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Recreate This
        </button>
      </div>
    </div>
  );
}

// Module-level cache so re-opening the Recreate modal is INSTANT — no "loading…" refetch every open.
// The cached lists render immediately; a background refresh runs only when older than the TTL.
const _srcCache: { winners: any[]; library: any[]; scraper: any[]; at: number } = { winners: [], library: [], scraper: [], at: 0 };
const _SRC_TTL = 5 * 60 * 1000;   // 5 minutes

// Source-picker modal (Vibelets-style): tabs Uploads / Library / Your winning ads.
// "Recreate This" hands the picked source + the (editable) command back to the parent's
// recreate() → /regen/generate. Closes on X, Escape, and backdrop click.
function RecreateModal({ initialTab, productMode, command, onCommand, onClose, onRecreate }: {
  initialTab: ModalTab; productMode: boolean; command: string; onCommand: (v: string) => void;
  onClose: () => void;
  onRecreate: (src: { videoUrl?: string; imageFile?: File; note?: string; label?: string }) => Promise<void>;
}) {
  const [tab, setTab] = useState<ModalTab>(initialTab);
  const [winners, setWinners] = useState<any[]>(_srcCache.winners);
  const [library, setLibrary] = useState<any[]>(_srcCache.library);
  const [scraper, setScraper] = useState<any[]>(_srcCache.scraper);   // Meta Ad Library competitor winners
  const [loading, setLoading] = useState(_srcCache.at === 0);   // spinner ONLY on the first-ever load
  const [file, setFile] = useState<File | null>(null);
  const [productUrl, setProductUrl] = useState('');
  const [busy, setBusy] = useState('');   // key of the source currently dispatching
  const [err, setErr] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Serve the cache instantly; only hit the network when the cache is empty or older than the TTL.
    if (_srcCache.at > 0 && Date.now() - _srcCache.at < _SRC_TTL) { setLoading(false); return; }
    Promise.all([
      // Top 20 winners by ROI only.
      regenApi.ourWinners({ limit: 20 }).then(r => { const d = (r.data.data || []).slice(0, 20); _srcCache.winners = d; setWinners(d); }).catch(() => {}),
      // Top 20 reusable VIDEO reference clips — not audio/voice or image-only system assets.
      regenApi.getAssets({ media: 'video', page_size: 20 }).then(r => { const d = (r.data.data || []).slice(0, 20); _srcCache.library = d; setLibrary(d); }).catch(() => {}),
      // Top 20 competitor winners scraped from the Meta Ad Library (all verticals).
      regenApi.scraperWinners('', 20).then(r => { const d = (r.data.winners || []).slice(0, 20); _srcCache.scraper = d; setScraper(d); }).catch(() => {}),
    ]).finally(() => { _srcCache.at = Date.now(); setLoading(false); });
  }, []);

  const go = async (key: string, src: { videoUrl?: string; imageFile?: File; note?: string; label?: string }) => {
    setBusy(key); setErr('');
    try { await onRecreate(src); }   // success unmounts the modal (parent closes it)
    catch (e: any) { setErr(e?.response?.data?.error || e.message || 'failed to start'); setBusy(''); }
  };

  const TABS: { id: ModalTab; label: string; icon: any }[] = [
    { id: 'uploads', label: 'Upload image', icon: Upload },
    { id: 'library', label: 'Pick a reference', icon: LibraryBig },
    { id: 'scraper', label: 'Meta Ads Library', icon: Trophy },
    { id: 'winners', label: 'Your winning ads', icon: Trophy },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Recreate from a reference</h2>
            <p className="text-sm text-gray-500 mt-0.5">Pick a source, then Recreate This — ORBIT builds a fresh ad from it.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-accent"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="px-5 pt-4">
          <label className="text-[11px] font-medium text-gray-500">Your instruction (prefilled — edit before you recreate)</label>
          <textarea value={command} onChange={e => onCommand(e.target.value)} rows={2}
            className="mt-1 w-full rounded-xl border border-border bg-transparent p-2.5 text-sm resize-none focus:outline-none" />
        </div>

        <div className="flex items-center gap-1 px-5 pt-3">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1.5 transition ${tab === t.id ? 'bg-primary text-white' : 'text-gray-400 hover:bg-accent'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {err && <div className="mx-5 mt-3 text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'uploads' && (
            <div className="space-y-4">
              {productMode && (
                <div className="rounded-xl border border-border p-3 space-y-2">
                  <div className="text-xs font-medium text-gray-500 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Product URL</div>
                  <input value={productUrl} onChange={e => setProductUrl(e.target.value)} placeholder="https://yourstore.com/product"
                    className="w-full rounded-lg border border-border bg-transparent p-2 text-sm" />
                  <button disabled={!productUrl.trim() || !!busy} onClick={() => go('product', { note: `Product page: ${productUrl.trim()}` })}
                    className="text-xs px-3 py-1.5 rounded-lg bg-primary text-white font-medium disabled:opacity-50 inline-flex items-center gap-1">
                    {busy === 'product' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Recreate from URL
                  </button>
                  <p className="text-[11px] text-gray-400">ORBIT reads the page URL as context for the ad. It doesn't auto-pull the product photos yet — add a reference image below for the best result.</p>
                </div>
              )}
              <div className="rounded-xl border-2 border-dashed border-border p-6">
                {file ? (
                  <div className="flex items-center gap-4">
                    <img src={URL.createObjectURL(file)} alt="" className="w-24 h-24 object-cover rounded-lg border border-border" />
                    <div className="space-y-2">
                      <div className="text-sm text-gray-300 truncate max-w-[220px]">{file.name}</div>
                      <div className="flex gap-2">
                        <button disabled={!!busy} onClick={() => go('file', { imageFile: file })}
                          className="text-xs px-3 py-1.5 rounded-lg bg-primary text-white font-medium disabled:opacity-50 inline-flex items-center gap-1">
                          {busy === 'file' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Recreate This
                        </button>
                        <button onClick={() => setFile(null)} className="text-xs px-3 py-1.5 rounded-lg border border-border text-gray-400">Remove</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center gap-1 cursor-pointer text-center">
                    <Upload className="w-6 h-6 text-gray-400" />
                    <span className="text-sm text-gray-300">Upload a reference image — <span className="text-primary">browse</span></span>
                    <span className="text-[11px] text-gray-400">A product photo or a still from the ad you want to recreate.</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
                  </label>
                )}
              </div>
              <p className="text-[11px] text-gray-400">This tab takes an image. Have a reference <b>video</b>? Add it in Creative Library, then pick it from the “Pick a reference” tab.</p>
            </div>
          )}

          {tab === 'library' && (
            loading ? <ModalLoading /> :
            !library.length ? <ModalEmpty text="No reference clips available yet — ingest a video into your Creative Library to reuse it here." /> :
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {library.map(a => <SourceCard key={a.id} url={a.play_url} title={a.character_desc || a.name}
                busy={busy === a.id} disabled={!!busy} onGo={() => go(a.id, { videoUrl: a.play_url })} />)}
            </div>
          )}

          {tab === 'scraper' && (
            loading ? <ModalLoading /> :
            !scraper.length ? <ModalEmpty text="No scraped competitor winners yet — seed the daily Meta Ad Library scrape." /> :
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {scraper.map((w, i) => <SourceCard key={i} url={w.url} title={w.hook || w.brand || `winner ${Math.round(w.score || 0)}`}
                sub={w.vertical || 'meta ad'} busy={busy === 'sc' + i} disabled={!!busy} onGo={() => go('sc' + i, { videoUrl: w.url })} />)}
            </div>
          )}

          {tab === 'winners' && (
            loading ? <ModalLoading /> :
            !winners.length ? <ModalEmpty text="No winning creatives found." /> :
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {winners.map(w => <SourceCard key={w.creative_id} url={w.download_url} title={w.filename} sub={`ROI ${fmtRoi(w.roas)} · $${w.spend}`}
                busy={busy === w.creative_id} disabled={!!busy} onGo={() => go(w.creative_id, { videoUrl: w.download_url, label: w.filename })} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AutoStudio;
